import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '../blog/articles';
import SEOHead from '../components/SEOHead';

const BASE_URL = 'https://analizaprawna.eu';

export default function BlogIndexPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const jsonLd = [
    {
      '@type': 'Blog',
      name: 'Blog AnalizaPrawna — Poradnik prawny',
      description: 'Praktyczne artykuły o polskim prawie. Odszkodowania, prawa najemcy, alimenty, mobbing, reklamacje — napisane przystępnym językiem.',
      url: `${BASE_URL}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'AnalizaPrawna',
        url: BASE_URL,
      },
      blogPost: articles.map((a) => ({
        '@type': 'BlogPosting',
        headline: a.title,
        url: `${BASE_URL}/blog/${a.slug}`,
        datePublished: a.publishedAt,
        dateModified: a.updatedAt,
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Strona główna', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="Blog — Poradnik prawny | AnalizaPrawna"
        description="Praktyczne artykuły o polskim prawie. Odszkodowania, prawa najemcy, alimenty, mobbing, reklamacje — napisane przystępnym językiem."
        canonicalUrl={`${BASE_URL}/blog`}
        jsonLd={jsonLd}
      />

      <div className="-mx-4 sm:-mx-6 lg:-mx-8">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 dark:from-blue-800 dark:via-blue-900 dark:to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {t('blog.guide')}
              </h1>
              <p className="text-lg text-blue-100 leading-relaxed">
                {t('blog.heroDescription')}
              </p>
            </div>
          </div>
        </section>

        {/* Articles grid */}
        <section className="py-12 sm:py-16 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  className="group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg transition-all overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-3">
                      <time dateTime={article.publishedAt}>
                        {new Date(article.publishedAt).toLocaleDateString(locale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </time>
                      <span>·</span>
                      <span>{article.readingTimeMinutes} {t('common.readingTime')}</span>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 text-sm font-medium text-blue-600 dark:text-blue-400 group-hover:underline">
                      {t('common.readMore')}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 sm:py-16 bg-white dark:bg-slate-800">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {t('blog.ctaTitle')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {t('blog.ctaDescription')}
            </p>
            <Link
              to="/search"
              className="inline-block px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors text-lg"
            >
              {t('blog.ctaButton')}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
